import React, { useEffect, useRef, useState } from "react";
import { X, Camera, Loader2, AlertTriangle } from "lucide-react";

// Live in-app camera: desktop webcam + phone (rear camera by default).
// Calls onCapture(dataUrl) with a JPEG data URL; onClose to cancel.
export const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Camera access was blocked. Please allow the camera in your browser, or use Add photo instead."
            : "No camera available. Please use Add photo instead."
        );
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const max = 1200;
    const ratio = Math.min(max / v.videoWidth, max / v.videoHeight, 1);
    const w = Math.round(v.videoWidth * ratio);
    const h = Math.round(v.videoHeight * ratio);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    onCapture(c.toDataURL("image/jpeg", 0.85));
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black flex flex-col" data-testid="camera-capture">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="font-heading text-lg inline-flex items-center gap-2"><Camera className="h-5 w-5" /> Take a photo</span>
        <button onClick={onClose} data-testid="camera-capture-close" className="text-white/90 hover:text-brand-yellow"><X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden px-4">
        {error ? (
          <div className="max-w-sm text-center text-white/90" data-testid="camera-capture-error">
            <AlertTriangle className="h-10 w-10 mx-auto text-brand-yellow" />
            <p className="mt-3 text-sm">{error}</p>
            <button onClick={onClose} className="mt-4 bg-white text-brand-navy font-bold rounded-full px-5 h-10">Close</button>
          </div>
        ) : (
          <div className="relative w-full max-w-md aspect-square bg-slate-900 rounded-2xl overflow-hidden ring-2 ring-white/20">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
            {!ready && <div className="absolute inset-0 flex items-center justify-center text-white/70"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          </div>
        )}
      </div>
      {!error && (
        <div className="flex items-center justify-center pb-8 pt-4">
          <button
            onClick={capture}
            disabled={!ready}
            data-testid="camera-capture-btn"
            aria-label="Capture photo"
            className="h-16 w-16 rounded-full bg-white ring-4 ring-white/40 active:scale-95 transition disabled:opacity-50"
          />
        </div>
      )}
    </div>
  );
};
